import React, { useEffect, useState } from "react";
import { ShieldCheck, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { FLAGS } from "@/lib/utils";
import VerificationDocList, { ProgressBar } from "@/components/VerificationDocList";

const COUNTRIES = ["Nigeria", "Ghana", "Kenya"];
const OVERALL = {
  not_started: { label: "Not started", cls: "bg-neutral-100 text-neutral-700" },
  incomplete: { label: "Incomplete", cls: "bg-amber-50 text-amber-800" },
  pending_review: { label: "Under review", cls: "bg-sky-50 text-sky-700" },
  action_required: { label: "Action required", cls: "bg-red-50 text-red-700" },
  approved: { label: "Verified", cls: "bg-propera-light text-propera" },
};

export default function CompanyVerification() {
  const [st, setSt] = useState(null);

  async function load() {
    const { data } = await api.get("/verification/company");
    setSt(data);
  }
  useEffect(() => { load(); }, []);

  async function pickCountry(c) {
    try {
      const { data } = await api.patch("/verification/company/country", { country: c });
      setSt(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  if (!st) return <p className="text-sm text-neutral-500 text-center py-20">Loading…</p>;
  const overall = OVERALL[st.overall_status] || OVERALL.not_started;
  const country = COUNTRIES.includes(st.country) ? st.country : null;

  return (
    <div className="max-w-4xl mx-auto" data-testid="company-verification-page">
      <p className="text-xs uppercase tracking-[0.18em] text-neutral-500 font-semibold">Verification</p>
      <div className="flex items-start justify-between gap-4 flex-wrap mt-2">
        <h1 className="text-4xl font-bold text-neutral-900">Company verification</h1>
        <span data-testid="company-overall-status" className={`px-3 py-1.5 rounded-full text-xs font-semibold ${overall.cls}`}>{overall.label}</span>
      </div>
      <p className="text-neutral-500 mt-2 max-w-2xl">Investors only see developments from verified developers. Upload the registration documents required in the country your company is incorporated in.</p>

      {st.verified && (
        <div className="mt-6 rounded-2xl border border-propera/30 bg-propera-light/60 p-4 flex items-center gap-3" data-testid="company-verified-banner">
          <ShieldCheck className="text-propera" size={20} />
          <p className="text-sm text-neutral-800"><b>Your company is verified.</b> A Verified Developer badge now appears on your listings.</p>
        </div>
      )}

      <div className="mt-8">
        <p className="text-xs font-semibold text-neutral-700">Country of incorporation</p>
        <div className="flex flex-wrap gap-2 mt-2">
          {COUNTRIES.map((c) => (
            <button key={c} type="button" disabled={st.verified} data-testid={`company-country-${c}`} onClick={() => pickCountry(c)} className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors disabled:cursor-not-allowed ${country === c ? "bg-propera text-white border-propera" : "bg-white border-neutral-200 hover:border-neutral-300"}`}>
              {FLAGS[c] || ""} {c}
            </button>
          ))}
        </div>
      </div>

      <div className="soft-card p-5 mt-6"><ProgressBar done={st.required_uploaded} total={st.required_total} testId="company-progress" /></div>

      <div className="mt-4">
        <VerificationDocList items={st.items} uploadUrl="/verification/company/documents" onChanged={load} />
      </div>

      <p className="text-xs text-neutral-500 mt-4 flex items-center gap-1.5"><Lock size={12} /> Documents are stored privately and are only visible to you and the Propera compliance team. PDF, JPG, PNG or WebP · max 10 MB.</p>
    </div>
  );
}
